/**
 * สกิลติดตัว (passive) — ลงแต้มได้เหมือนสกิลอื่น แต่กดใช้ไม่ได้ ผลติดตัวตลอด
 *
 * - ข้อมูลอยู่ใน `classes.json` ที่ `SkillDef.passive` (kind = "passive") ตามกฎข้อ 4
 * - ผลคิด **ต่อเลเวลสกิล** (`perLv` × เลเวล) เช่น STR +1/เลเวล, ATK +2%/เลเวล
 * - มีเงื่อนไขท่าถือได้ (`when`) เช่น "+โจมตีเมื่อถืออาวุธ 1 มือ" "+ป้องกันเมื่อถือโล่"
 * - รวมเข้าค่ารองที่จุดเดียวกับของสวมใส่: สเตตัสบวกก่อนคิด ตัวปรับรวมด้วย `mergeModifiers`
 *   server ใช้ตัดสินจริง client ใช้แสดงในหน้าต่างตัวละคร/สกิล ด้วยฟังก์ชันเดียวกันนี้
 *
 * pure function ทั้งหมด
 */
import { isOffhandType } from "./equip.js";
import { ELEMENT_NAMES, STAT_KEYS, WEAPON_TYPE_NAMES, } from "./types.js";
/** จำนวนมือของอาวุธที่ถือ ดูจากชนิด (`*_2h` = สองมือ) */
export function heldHands(held) {
    if (!held.weapon || isOffhandType(held.weapon))
        return undefined;
    return held.weapon.endsWith("_2h") ? 2 : 1;
}
/** เงื่อนไขท่าถือของสกิลติดตัวนี้ผ่านไหม (ไม่มีเงื่อนไข = ผ่านเสมอ) */
export function passiveActive(p, held) {
    const w = p.when;
    if (!w)
        return true;
    if (w.hands && heldHands(held) !== w.hands)
        return false;
    if (w.weapon?.length && !(held.weapon && w.weapon.includes(held.weapon)))
        return false;
    if (w.offhand?.length && !(held.offhand && w.offhand.includes(held.offhand)))
        return false;
    return true;
}
/**
 * รวมผลสกิลติดตัวทุกตัวที่ลงแต้มไว้และเงื่อนไขท่าถือผ่าน
 * @param skills สกิลทั้งหมดของอาชีพ (รวมสกิลขั้นต้นของอาชีพขั้นสูง — `getAllSkillsForClass`)
 */
export function passiveTotals(skills, levels, held) {
    const out = { mods: {}, stats: {}, elementDmg: {} };
    const lvOf = (id) => {
        const src = levels;
        return Number(typeof src.get === "function" ? src.get(id) ?? 0 : levels[id] ?? 0);
    };
    for (const sk of skills) {
        if (sk.kind !== "passive" || !sk.passive)
            continue;
        const lv = lvOf(sk.id);
        if (lv <= 0 || !passiveActive(sk.passive, held))
            continue;
        addEffect(out, sk.passive.perLv, lv);
    }
    return out;
}
function addEffect(out, e, lv) {
    const m = out.mods;
    for (const k of STAT_KEYS) {
        const v = e.stats?.[k];
        if (v)
            out.stats[k] = (out.stats[k] ?? 0) + Math.floor(v * lv);
    }
    // ค่าที่เป็นเปอร์เซ็นต์เก็บเป็นเศษส่วนต่อเลเวล (0.02 = +2%/เลเวล) แปลงเป็นตัวคูณ
    if (e.hpPct)
        m.hpMul = (m.hpMul ?? 1) * (1 + e.hpPct * lv);
    if (e.mpPct)
        m.mpMul = (m.mpMul ?? 1) * (1 + e.mpPct * lv);
    if (e.atkPct)
        m.atkMul = (m.atkMul ?? 1) * (1 + e.atkPct * lv);
    if (e.matkPct)
        m.matkMul = (m.matkMul ?? 1) * (1 + e.matkPct * lv);
    if (e.aspdPct)
        m.aspdMul = (m.aspdMul ?? 1) * (1 + e.aspdPct * lv);
    if (e.def)
        m.flatDef = (m.flatDef ?? 0) + e.def * lv;
    if (e.crit)
        m.flatCrit = (m.flatCrit ?? 0) + e.crit * lv;
    if (e.hit)
        m.flatHit = (m.flatHit ?? 0) + e.hit * lv;
    if (e.flee)
        m.flatFlee = (m.flatFlee ?? 0) + e.flee * lv;
    if (e.block)
        m.flatBlock = (m.flatBlock ?? 0) + e.block * lv;
    for (const [el, v] of Object.entries(e.elementDmg ?? {})) {
        out.elementDmg[el] = (out.elementDmg[el] ?? 0) + v * lv;
    }
}
const pct = (x) => `${Math.round(x * 1000) / 10}%`;
/** คำบรรยายผลที่เลเวลนั้น สร้างจากข้อมูล (ข้อความจึงตรงกับผลจริงเสมอ) */
export function describePassive(p, lv) {
    const e = p.perLv;
    const L = Math.max(1, lv);
    const bits = [];
    for (const k of STAT_KEYS) {
        const v = e.stats?.[k];
        if (v)
            bits.push(`${k} +${Math.floor(v * L)}`);
    }
    if (e.hpPct)
        bits.push(`HP สูงสุด +${pct(e.hpPct * L)}`);
    if (e.mpPct)
        bits.push(`MP สูงสุด +${pct(e.mpPct * L)}`);
    if (e.atkPct)
        bits.push(`ATK +${pct(e.atkPct * L)}`);
    if (e.matkPct)
        bits.push(`MATK +${pct(e.matkPct * L)}`);
    if (e.aspdPct)
        bits.push(`ความเร็วโจมตี +${pct(e.aspdPct * L)}`);
    if (e.def)
        bits.push(`DEF +${e.def * L}`);
    if (e.crit)
        bits.push(`CRIT +${e.crit * L}`);
    if (e.hit)
        bits.push(`HIT +${e.hit * L}`);
    if (e.flee)
        bits.push(`FLEE +${e.flee * L}`);
    if (e.block)
        bits.push(`บล็อก +${pct(e.block * L)}`);
    for (const [el, v] of Object.entries(e.elementDmg ?? {})) {
        bits.push(`ดาเมจ${ELEMENT_NAMES[el]} +${pct(v * L)}`);
    }
    return bits.join(" · ") + (p.when ? ` (${describeWhen(p)})` : "");
}
/** เงื่อนไขท่าถือเป็นข้อความ เช่น "เมื่อถืออาวุธ 1 มือ" */
export function describeWhen(p) {
    const w = p.when;
    if (!w)
        return "";
    const parts = [];
    if (w.hands)
        parts.push(`อาวุธ ${w.hands} มือ`);
    if (w.weapon?.length)
        parts.push(w.weapon.map((t) => WEAPON_TYPE_NAMES[t] ?? t).join("/"));
    if (w.offhand?.length)
        parts.push(w.offhand.map((t) => WEAPON_TYPE_NAMES[t] ?? t).join("/"));
    return `เมื่อถือ${parts.join(" + ")}`;
}
//# sourceMappingURL=passives.js.map